import type { FC, ComponentType } from "react";
import { Box, Alert, Typography } from "@mui/material";
import { Loading } from "src/components/utils/Loading";
import { RuntimePuckExtension, ExtensionContext } from "src/api/client-api/puckExtensions/useExtensions";
import { Render } from "@measured/puck";
import { rootConfig } from "src/components/Puck/config";

type PuckExtensionRendererProps = {
    extension: RuntimePuckExtension;
    context?: ExtensionContext;
    isLoading?: boolean;
    showMetadata?: boolean;
};

/**
 * Composant pour rendre une extension Puck individuelle
 * Version temporaire simplifiée en attendant la stabilisation de la config Puck
 */
export const PuckExtensionRenderer = (props: PuckExtensionRendererProps) => {
    const { extension, isLoading = false, showMetadata = false } = props;
    if (isLoading) {
        return <Loading />;
    }

    if (!extension?.puckData) {
        return null;
    }

    try {
        return (
            <Box
                className="puck-extension"
                data-extension-id={extension.id}
                sx={{ "& > div": { minHeight: "auto !important" } }}
            >
                {showMetadata && (
                    <Box
                        sx={{
                            fontSize: "0.75rem",
                            color: "text.secondary",
                            mb: 1,
                            p: 1,
                            backgroundColor: "grey.50",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="caption">
                            Extension: {extension.name} (Point: {extension.extensionPoint})
                        </Typography>
                    </Box>
                )}

                <Render config={rootConfig} data={parsePuckData(extension.puckData)} />
            </Box>
        );
    } catch (error) {
        console.error("Error rendering Puck extension:", error);
        return (
            <Alert severity="error">
                <Typography variant="body2">Impossible de rendre l'extension "{extension.name}"</Typography>
            </Alert>
        );
    }
};

const EMPTY_PUCK_DATA: Record<string, unknown> = { content: [], root: { props: {} } };

const parsePuckData = (raw: string | null | undefined): Record<string, unknown> => {
    if (!raw) return EMPTY_PUCK_DATA;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && "content" in parsed && Array.isArray(parsed.content)) {
            return parsed;
        }
        return EMPTY_PUCK_DATA;
    } catch {
        return EMPTY_PUCK_DATA;
    }
};

/**
 * HOC pour ajouter une gestion d'erreur autour d'un composant d'extension
 */
export const withExtensionErrorBoundary = <P extends object>(Component: ComponentType<P>): FC<P> => {
    return (props: P) => {
        try {
            return <Component {...props} />;
        } catch (error) {
            console.error("Extension component error:", error);
            return (
                <Alert severity="error">
                    <Typography variant="body2">Erreur dans le composant d'extension</Typography>
                </Alert>
            );
        }
    };
};
